import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Splash } from "./components/Splash";

import Landing from "./pages/Landing";
import Join from "./pages/Join";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";

import PortalLayout from "./components/PortalLayout";
import PortalDashboard from "./pages/portal/Dashboard";
import PortalSides from "./pages/portal/Sides";
import PortalSideReader from "./pages/portal/SideReaderPage";
import PortalClasses from "./pages/portal/Classes";
import PortalBilling from "./pages/portal/Billing";
import PortalProfile from "./pages/portal/Profile";
import PortalRequests from "./pages/portal/Requests";

import ReefLayout from "./components/ReefLayout";
import ReefDashboard from "./pages/reef/Dashboard";
import ReefRegistrations from "./pages/reef/Registrations";
import ReefClasses from "./pages/reef/Classes";
import ReefClassDetail from "./pages/reef/ClassDetail";
import ReefSides from "./pages/reef/Sides";
import ReefStudents from "./pages/reef/Students";
import ReefBilling from "./pages/reef/Billing";
import ReefRequests from "./pages/reef/Requests";
import ReefSignups from "./pages/reef/Signups";
import ReefApiAccess from "./pages/reef/ApiAccess";
import ReefHowItWorks from "./pages/reef/HowItWorks";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash text="Checking your session…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "admin" && user.role !== "admin") return <Navigate to="/portal" replace />;
  if (role === "student" && user.role === "admin") return <Navigate to="/reef" replace />;
  return children;
}

function AppRouter() {
  useLocation();
  // Process Google OAuth return BEFORE any route/auth check (handles race conditions).
  if (window.location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing variant="whitefire" />} />
      <Route path="/tvi" element={<Landing variant="tvi" />} />
      <Route path="/join" element={<Join />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/signup" element={<Auth mode="signup" />} />

      <Route
        path="/portal"
        element={
          <Protected role="student">
            <PortalLayout />
          </Protected>
        }
      >
        <Route index element={<PortalDashboard />} />
        <Route path="sides" element={<PortalSides />} />
        <Route path="sides/:id" element={<PortalSideReader />} />
        <Route path="classes" element={<PortalClasses />} />
        <Route path="requests" element={<PortalRequests />} />
        <Route path="billing" element={<PortalBilling />} />
        <Route path="profile" element={<PortalProfile />} />
      </Route>

      <Route
        path="/reef"
        element={
          <Protected role="admin">
            <ReefLayout />
          </Protected>
        }
      >
        <Route index element={<ReefDashboard />} />
        <Route path="registrations" element={<ReefRegistrations />} />
        <Route path="requests" element={<ReefRequests />} />
        <Route path="signups" element={<ReefSignups />} />
        <Route path="api-access" element={<ReefApiAccess />} />
        <Route path="how-it-works" element={<ReefHowItWorks />} />
        <Route path="classes" element={<ReefClasses />} />
        <Route path="classes/:id" element={<ReefClassDetail />} />
        <Route path="sides" element={<ReefSides />} />
        <Route path="students" element={<ReefStudents />} />
        <Route path="billing" element={<ReefBilling />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: "#F7F3EC",
              border: "1px solid #E5DECF",
              color: "#1A1A1A",
              borderRadius: 0,
              fontFamily: "Mulish, sans-serif",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
